import { ArticleDate } from '@components/atoms/ArticleDate/ArticleDate';
import { EntitySectionTitle } from '@components/atoms/EntitySectionTitle/EntitySectionTitle';
import type { LinkProps } from '@components/atoms/Link/Link';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { ArticleInfos } from '@components/molecules/ArticleInfos/ArticleInfos';
import { ImageCard } from '@components/molecules/ImageCard/ImageCard';
import { SocialButtons } from '@components/molecules/SocialButtons/SocialButtons';
import { ArticleTeaserCard } from '@components/organisms/ArticleTeaserCard/ArticleTeaserCard';
import { AuthorBoxBottom } from '@components/organisms/AuthorBoxBottom/AuthorBoxBottom';
import { Breadcrumbs } from '@components/organisms/Breadcrumbs/Breadcrumbs';
import * as ContentElements from '@components/organisms/ContentElements/ContentElements';
import { NewsletterTeaser } from '@components/organisms/Newsletter/NewsletterTeaser';
import { Article as ArticleLayout } from '@components/templates/layouts/Article';
import { adPositions } from '@lib/adition/adPositions';
import { GetGlobalsDocument } from '@lib/graphql/generated';
import { fetchExtraArticleData } from '@lib/graphql/helpers/fetchExtraArticleData';
import { createUrqlClient } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { handleRedirect } from '@lib/redirects/redirect';
import { GetBySlugFullDocument, type Newsletter as NewsletterType } from '@src/lib/graphql/generated';
import { decodeOptions } from '@src/utils/urlHelpers';
import { getCookie } from '@utils/cookies';
import { createDate } from '@utils/dateHelpers';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
} from '@vgn-medien-holding/vgn-fe-components';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useInView } from 'react-intersection-observer';
import { NEWSLETTER_ACTIVE } from 'tvm-config';

// Artikel
const ad_settings_Artikel = {
  billboard: {
    id: 5256679,
    name: 'TVM_Artikel_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  right1: {
    id: 5256686,
    name: 'TVM_Unterseite_TVProgramm_HPA',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  left1: {
    id: 5256685,
    name: 'Left1',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  cad1: {
    id: 5256687,
    name: 'TVM_Artikel_MedRec',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_top: {
    id: 5256620,
    name: 'TVM_Mobile_Unterseite_Artikel_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256619,
    name: 'TVM_Mobile_Unterseite_Artikel_MedRec',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256620,
    name: 'TVM_Mobile_Unterseite_Artikel_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

function Article({ data, extraData = null, newsletter }) {
  const {
    content,
    published_at,
    created_at,
    updated_at,
    cover_image,
    headline,
    authors,
    disable_ads,
    hide_authors,
    hide_cover_image,
    hide_authors_bottom,
    hide_updated_at,
    teaser,
    ressort,
    similar_articles,
    metadata,
    oewa,
  } = data;
  data.article = true;

  const breadcrumbsPath: LinkProps[] = [
    { href: '/', title: 'TV-MEDIA.AT' },
    { href: '/magazin', title: 'MAGAZIN' },
    { href: `/${ressort?.slug}`, title: ressort?.title.toUpperCase() },
  ];

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Artikel',
    titel: headline,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || ressort?.slug === 'gewinnspiele' ? 'Games' : 'RedCont',
    oewaSubkontingent:
      oewa?.category_key || ressort?.slug === 'gewinnspiele' ? 'UnterhaltungGames' : 'KulturUndFreizeit',
    oewaProfilingkontingent: oewa?.profile_key || ressort?.slug === 'gewinnspiele' ? 'Gewinnspiele' : 'FilmUndKino',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || ressort?.slug === 'gewinnspiele' ? 'UnterhaltungGames' : 'RedCont'}
        categoryKey={oewa?.category_key || ressort?.slug === 'gewinnspiele' ? 'Games' : 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || ressort?.slug === 'gewinnspiele' ? 'Gewinnspiele' : 'FilmUndKino'}
      />
      <AditionInitialization data={{ ad_settings: ad_settings_Artikel }} />
      <Head>{Meta({ ...metadata, open_graph_image: cover_image })}</Head>

      <div className={'pt-10 text-gray-600 lg:grid lg:grid-cols-[1fr_auto_1fr]'}>
        <div className="relative hidden w-full justify-end pr-4 lg:flex">
          {!disable_ads && (
            <AditionTagPlacement
              tag={ad_settings_Artikel.left1}
              classProps={{ root: 'sticky top-28 mb-4 h-[600px] hidden 3xl:block' }}
            />
          )}
        </div>
        <main className="relative mx-auto w-full min-w-0 max-w-[1090px]">
          {!disable_ads && (
            <aside className="grid place-items-center">
              <AditionTagPlacement
                tag={ad_settings_Artikel.mobile_top}
                classProps={{ root: 'mb-4 md:hidden w-[300px]' }}
              />
            </aside>
          )}
          <div
            className={
              'mt-4 items-center justify-between border-gray-300 px-6 py-4 md:px-20 lg:mx-0 lg:flex lg:border-y lg:px-0'
            }
          >
            <Breadcrumbs breadcrumbs={breadcrumbsPath} />
            <div className="pt-2 lg:pt-0">
              {created_at ? (
                <ArticleDate
                  createdDate={createDate(published_at ? published_at : created_at)}
                  updatedDate={createDate(updated_at ? updated_at : created_at)}
                  hideUpdatedAt={hide_updated_at}
                />
              ) : (
                ''
              )}
            </div>
          </div>

          <section className="relative mt-8 px-5 md:mt-25 md:px-20 lg:px-32">
            <Title level={1} classProps={{ heading: 'font-medium' }}>
              {headline}
            </Title>
            <div className={'prose md:prose-lg my-9'} dangerouslySetInnerHTML={{ __html: teaser }} />
            <SocialButtons classProps={{ root: 'py-6 md:py-0 md:pb-6' }} />
            <hr className="mb-5 border-gray-300 border-t lg:mb-10" />
            <ArticleInfos article={data} />
          </section>
          {!hide_cover_image && cover_image?.url && (
            <div className={'mx-4 my-9 rounded-xl bg-white shadow-card-sm sm:mx-8 lg:mx-0'}>
              <ImageCard
                imageSrc={cover_image.url}
                imageAlt={cover_image.alt}
                priority
                width={2560}
                height={1440}
                imageMode="cover"
                classProps={{
                  ImageCard: 'rounded-lg',
                  Image: 'rounded-t-lg w-full',
                  ImageText: 'text-sm',
                  root: 'rounded-lg',
                }}
                sizes="(min-width: 1020px) 1280px, 100vw"
                description={cover_image.caption}
                copyright={cover_image.copyright_text}
              />
            </div>
          )}
          <section className="w-full px-4 pb-20 sm:px-8 lg:px-0">
            <div className={'w-full'}>
              <ArticleBody content={content} extraData={extraData} disable_ads={disable_ads} />
            </div>
            {similar_articles?.length > 0 && (
              <section className="mt-16 content-element-padding">
                <EntitySectionTitle heading={false} classProps={{ root: 'mt-0' }}>
                  Ähnliche Artikel
                </EntitySectionTitle>
                <div className="tvm-scrollbar w-full min-w-0 overflow-x-scroll pb-8">
                  <div className="grid h-full grid-flow-col gap-3">
                    {similar_articles.map((article, index) => (
                      <div className="h-full min-h-[28rem] w-60 sm:w-72 md:w-96" key={index}>
                        <ArticleTeaserCard
                          article={article}
                          hideCategory
                          classProps={{
                            root: 'flex-none w-full h-full min-w-0 min-h-[18rem]',
                            ImageContainer: 'w-full h-full px-6 pt-6',
                            Image: 'relative w-full h-full rounded-lg overflow-hidden aspect-teaser lg:aspect-video',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
            {!hide_authors && !hide_authors_bottom && <AuthorBoxBottom authors={authors} />}
          </section>
        </main>
        {!disable_ads && (
          <div className="relative hidden w-full justify-start pl-4 lg:flex">
            <AditionTagPlacement
              tag={adPositions[15]}
              classProps={{ root: 'sticky top-28 mb-4 h-[600px] hidden 3xl:block' }}
            />
          </div>
        )}
      </div>
      {NEWSLETTER_ACTIVE && newsletter?.id && <NewsletterTeaser classProps={{ root: 'my-15' }} />}
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery } = createUrqlClient();

  let data: any;
  let extraData: any;
  let newsletter: NewsletterType = { id: null };

  const options = decodeOptions(params?.slug);

  try {
    const { data: globalData } = await ssrQuery({
      query: GetGlobalsDocument,
      variables: {},
    });

    try {
      newsletter = globalData != null && globalData.globals !== null ? (globalData.globals as any).newsletter : '';
    } catch (err) {
      console.error(err);
      newsletter = null;
    }

    const { data: entryData } = await ssrQuery({
      query: GetBySlugFullDocument,
      variables: {
        portal: 'tvmedia',
        slug: `${params.categoryOrPage}/${options.slug}`,
      },
      authToken: options?.params?.token,
    });
    data = entryData?.findBySlug;
  } catch (err) {
    console.log(err);
    return { notFound: true };
  }

  if (!data) return { notFound: true };

  // Handle redirect result
  if (data?.redirect_type || data?.metadata?.redirect) {
    return handleRedirect(
      data?.redirect_type,
      data?.redirect_target != null ? data?.redirect_target : data?.metadata?.redirect,
    );
  }

  if (data?.__typename === 'Article') {
    extraData = await fetchExtraArticleData(data);
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      extraData,
      newsletter,
      // urqlState: ssrCache.extractData(),
    },
  };
};

function ArticleElement({ children, index }) {
  const { ref: inViewRef, inView } = useInView({
    rootMargin: `${3000 / index}px 0px`,
    triggerOnce: true,
  });

  if (index < 20) return children;

  return (
    <div className="min-h-4" ref={inViewRef}>
      {inView ? children : null}
    </div>
  );
}

export function ArticleBody({ content, extraData = [], disable_ads = false }) {
  const toc = content?.find((e) => e.__typename === 'ContentElementTableOfContents')?.list;

  const tocFlattened = [];
  if (toc) {
    const flattenElement = (el) => {
      if (el.children) {
        el.children.forEach((e) => {
          flattenElement(e);
        });
        tocFlattened.push(...el.children.map((f) => f.anchor));
      }
    };

    toc.forEach((e) => {
      tocFlattened.push(e.anchor);
      flattenElement(e);
    });
  }
  let tocCount = -1;

  let ad_count = 0;
  let textElement_count = 0;

  return (
    <div className="mt-16 space-y-16">
      {content?.map((element, idx) => {
        if (element.__typename === 'ContentElementText') textElement_count++;
        if (element.headline) tocCount++;
        return (
          <div className="scroll-mt-32" key={idx} id={tocFlattened?.[tocCount]}>
            <div className="scroll-mt-32" id={element.anchor}>
              <ArticleElement index={idx}>
                {element.headline && (
                  <Title level={element.headline_level} classProps={{ heading: 'pb-4 content-element-padding' }}>
                    {element.headline}
                  </Title>
                )}
                {/* biome-ignore lint/performance/noDynamicNamespaceImportAccess: dynamic CMS typename lookup */}
                {ContentElements[element.__typename] && ContentElements[element.__typename](element, extraData)}
                {!disable_ads &&
                  (idx === 0 ||
                    (textElement_count >= 3 &&
                      element.__typename === 'ContentElementText' &&
                      idx + 1 !== content.length &&
                      content[idx + 1].__typename === 'ContentElementText' &&
                      ad_count < 3)) && (
                    <div className="grid place-items-center">
                      <AditionTagWithFallback
                        tag={ad_settings_Artikel.billboard}
                        tagCount={ad_count++}
                        fallback={
                          ad_count % 2 === 0 ? ad_settings_Artikel.mobile_middle1 : ad_settings_Artikel.mobile_middle2
                        }
                        breakpoint={'lg'}
                        classProps={{ root: 'm-auto mt-16' }}
                      />
                    </div>
                  )}
              </ArticleElement>
            </div>
          </div>
        );
      })}
    </div>
  );
}

Article.PageLayout = ArticleLayout;

export default Article;
